// react
import { SyntheticEvent, useCallback, useContext, useState } from 'react';

// types
import { Act, Contract, Document, Entity, Frame, Loan, Pagination } from '../../../@types';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Grid, Paper } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs } from '../../ui/Tabs/Tabs';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// dialogs
import { DialogAddContract } from '../../dialogs/DialogAddContract/DialogAddContract';
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogEditSupplier } from '../../dialogs/DialogEditSupplier/DialogEditSupplier';
import { DialogUpdateEntityStatus } from '../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// tables
import { TableBasicActs } from '../../tables/basic/TableBasicActs/TableBasicActs';
import { TableBasicContracts } from '../../tables/basic/TableBasicContracts/TableBasicContracts';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';

// theme
import { theme } from '../../../theme';

// icons
import {
	ArrowCircleRightOutlined as IconArrowCircleRight,
	AssignmentOutlined as IconAssignment,
	DoDisturbOutlined as IconDoDisturb,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
	PaidOutlined as IconPaid,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
} from '@mui/icons-material';

// config
import { featureFlags } from '../../../config/featureFlags';

// utils
import { defaultPagination } from '../../../utils/defaults';

// props
export interface CardSupplierProps {
	frame?: Frame;
	supplier: Entity;
	refetchEntities?: () => void;
}

export const CardSupplier = ({ frame, supplier, refetchEntities }: CardSupplierProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [acts, setActs] = useState<Act[]>([]);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [searchParams, setSearchParams] = useSearchParams();
	const [tabValue, setTabValue] = useState('contracts');

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogSupplier = searchParams.get('dialogSupplier');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// handlers
	const handleOpenDialogEditSupplier = useCallback(() => {
		searchParams.set('dialog', 'editSupplier');
		searchParams.set('dialogSupplier', String(supplier.id));
		setSearchParams(searchParams);
	}, [supplier, searchParams, setSearchParams]);

	const handleOpenDialogUpdateSupplier = useCallback(() => {
		searchParams.set('dialog', 'updateSupplierStatus');
		searchParams.set('dialogSupplier', String(supplier.id));
		setSearchParams(searchParams);
	}, [supplier, searchParams, setSearchParams]);

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogSupplier');
		setSearchParams(searchParams);
	};

	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	// vars
	const btnMenuItems: MenuItemProps[] = [];
	const isFrameSupplier = frame === 'SUPPLIER';
	const tabs = [
		{
			label: t('contracts'),
			value: 'contracts',
		},
		{
			label: t('documents'),
			value: 'documents',
		},
	];

	if (featureFlags['ACTIVITIES']) {
		tabs.push({
			label: t('activities'),
			value: 'activities',
		});
	}

	if (userProjectPermissions?.contract?.create) {
		btnMenuItems.push({
			children: t('addContract'),
			disabled: project?.status === 'PENDING',
			icon: <IconAssignment />,
			onClick: () => {
				searchParams.set('dialog', 'addContract');
				searchParams.set('dialogSupplier', String(supplier.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.payment?.create) {
		btnMenuItems.push({
			children: t('recordPayment'),
			icon: <IconPaid />,
			onClick: () => {
				searchParams.set('dialog', 'recordPayment');
				searchParams.set('dialogSupplier', String(supplier.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsSupplier');
				searchParams.set('dialogSupplier', String(supplier.id));
				setSearchParams(searchParams);
			},
		});
	}

	btnMenuItems.push({
		children: t('seeDetails'),
		component: RouterLink,
		icon: <IconArrowCircleRight />,
		to: `/project/${project?.idWbg}/supplier/${supplier.id}`,
	});

	if (userProjectPermissions?.supplier?.update) {
		btnMenuItems.push({
			children: t('editSupplier'),
			icon: <IconModeEdit />,
			onClick: handleOpenDialogEditSupplier,
		});
	}

	if (userProjectPermissions?.supplier?.update) {
		let btnLabel = t('deactivateSupplier');
		let btnIcon = <IconDoDisturb color="error" />;

		if (supplier.wallet?.status === 'INACTIVE') {
			btnLabel = t('activateSupplier');
			btnIcon = <IconPowerSettingsNew color="success" />;
		}

		btnMenuItems.push({
			children: btnLabel,
			icon: btnIcon,
			onClick: handleOpenDialogUpdateSupplier,
		});
	}

	const parentEntity = loans?.[0]?.entities?.[0].parent;

	// fetch loans
	useFetch({
		isEnabled: Boolean(supplier),
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${supplier.id}&fields=id,entities&populate=entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch contracts
	const { isLoading: isLoadingContracts } = useFetch({
		isEnabled: Boolean(supplier),
		url: `${process.env.REACT_APP_API_URL}/contracts?entities=${supplier?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
	});

	// fetch documents
	const urlFetchDocs = `${process.env.REACT_APP_API_URL}/documents?entities=${supplier.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`;

	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(supplier && isExpanded && tabValue === 'documents'),
		url: urlFetchDocs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// fetch acts
	const urlFetchActs = `${process.env.REACT_APP_API_URL}/acts?entities=${supplier.id}`;

	const { isLoading: isLoadingActs } = useFetch({
		isEnabled: Boolean(supplier && isExpanded && tabValue === 'activities'),
		url: urlFetchActs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}
		},
	});

	return (
		<>
			<Paper sx={{ position: 'relative' }}>
				<Box
					sx={(theme) => {
						return { padding: theme.spacing(2) };
					}}>
					<Grid container alignItems="center" spacing={2}>
						<Grid item xs={12} lg={2 + (featureFlags['FINANCIAL_TRACEABILITY'] ? 0 : 0.25)}>
							<Grid alignItems="center" container spacing={2} wrap="nowrap">
								<Grid item>
									<IconCircle type="supplier" />
								</Grid>
								<Grid item>
									<TypographyCaption id={supplier.idExternal} />
									<LinkEntity entity={supplier} />
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12} lg={2 + (featureFlags['FINANCIAL_TRACEABILITY'] ? 0 : 0.25)}>
							<RowKeyValue
								label={t('wallet')}
								value={
									supplier.wallet?.address ? (
										<BlockchainHash hash={supplier.wallet.address} />
									) : (
										t('na')
									)
								}
							/>
						</Grid>
						{!isFrameSupplier && (
							<Grid item xs={12} lg={2}>
								<RowKeyValue
									label={t('entity')}
									value={<LinkEntity entity={parentEntity} hasEllipsis={true} />}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('status')}
								value={<ChipStatus status={supplier.wallet?.status || 'ACTIVE'} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue label={t('contracts')} value={contracts.length} />
						</Grid>
						<Grid item xs={12} lg={1 + (featureFlags['FINANCIAL_TRACEABILITY'] ? 0 : 0.25)}>
							<RowKeyValue
								label={t('transactionsEvidenced')}
								value={
									<ChipTraceability traceability={supplier.traceability} type="TRANSACTIONS" />
								}
							/>
						</Grid>
						{featureFlags['FINANCIAL_TRACEABILITY'] && (
							<Grid item xs={12} lg={1}>
								<RowKeyValue
									label={t('financialValueEvidenced')}
									value={<ChipTraceability traceability={supplier.traceability} type="FINANCIAL" />}
								/>
							</Grid>
						)}
						<Grid
							item
							xs={12}
							lg={isFrameSupplier ? 3 : 1.5 + (featureFlags['FINANCIAL_TRACEABILITY'] ? 0 : 0.25)}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										entity={supplier}
										activityTypes={['ENTITY_UPDATED', 'ENTITY_CREATED']}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={0.5}>
							<Grid container justifyContent={{ xs: 'space-between', lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="contracts">
							<TableBasicContracts data={contracts} isLoading={isLoadingContracts} />
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
						<TabPanel value="activities">
							<TableBasicActs data={acts} isLoading={isLoadingActs} />
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogAddContract
				entity={supplier}
				isOpen={paramDialog === 'addContract'}
				onClose={handleCloseDialog}
			/>
			<DialogAddTransaction
				DialogActionsProps={{ subtitle: { children: supplier.name } }}
				isOpen={paramDialog === 'recordPayment' && paramDialogSupplier === String(supplier.id)}
				onClose={handleCloseDialog}
				type="PAYMENT"
			/>
			<DialogUpdateEntityStatus
				entity={supplier}
				isOpen={
					paramDialog === 'updateSupplierStatus' && paramDialogSupplier === String(supplier.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
			<DialogEditSupplier
				DialogActionsProps={{ subtitle: { children: supplier.name } }}
				isOpen={paramDialog === 'editSupplier' && paramDialogSupplier === String(supplier.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
			<DialogUploadDocs
				entity={supplier}
				isOpen={paramDialog === 'uploadDocsSupplier' && paramDialogSupplier === String(supplier.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
		</>
	);
};
